import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth, homePathFor } from "@/lib/auth";
import api from "@/lib/api";
import {
  ChartLine, Ticket, ShieldCheck, GitBranch, Bug, HardDrives,
  GearSix, SignOut, BookOpen, Broadcast, UsersThree, Clock, LockKey, ClockClockwise, PenNib, ArrowLeft, ListChecks
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { ThemeToggle } from "@/components/ThemeToggle";
import { ModuleSwitcher } from "@/components/ModuleSwitcher";
import { IdleManager } from "@/components/IdleManager";
import { TrakLogo } from "@/components/TrakLogo";
import { Breadcrumbs, BreadcrumbProvider } from "@/components/Breadcrumbs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const STAFF = ["admin", "manager", "agent"];
const NAV = [
  { to: "/", label: "Dashboard", icon: ChartLine, exact: true, testid: "nav-dashboard", roles: STAFF },
  { to: "/incidents", label: "Incidents", icon: Ticket, testid: "nav-incidents", roles: STAFF },
  { to: "/problems", label: "Problems", icon: Bug, testid: "nav-problems", roles: STAFF },
  { to: "/changes", label: "Changes", icon: GitBranch, testid: "nav-changes", roles: STAFF },
  { to: "/assets", label: "Assets (CMDB)", icon: HardDrives, testid: "nav-assets", roles: STAFF },
  { to: "/kb", label: "Knowledge Base", icon: BookOpen, testid: "nav-kb", roles: STAFF },
  { to: "/reports", label: "Reports", icon: ChartLine, testid: "nav-reports", roles: ["admin", "manager", "agent", "timesheet_admin"] },
  { to: "/users", label: "Users & Skills", icon: UsersThree, testid: "nav-users", roles: ["admin", "timesheet_admin"] },
  { to: "/routing", label: "Routing Rules", icon: Broadcast, testid: "nav-routing", roles: ["admin", "manager"] },
  { to: "/security", label: "Security & Access", icon: LockKey, testid: "nav-security", roles: ["admin"] },
  { to: "/sessions", label: "Session Activity", icon: Clock, testid: "nav-sessions", roles: ["admin", "manager"] },
  { to: "/admin", label: "Admin", icon: GearSix, testid: "nav-admin", roles: ["admin", "manager", "agent"] },
  { to: "/soc", label: "SOC Center", icon: ShieldCheck, testid: "nav-soc", roles: ["admin"] },
  { to: "/timesheet", label: "Timesheet", icon: ClockClockwise, testid: "nav-timesheet", roles: ["admin", "manager", "agent", "timesheet_admin"], requiresTs: true },
  { to: "/tasks", label: "Tasks", icon: ListChecks, testid: "nav-tasks", roles: ["admin", "manager", "agent", "timesheet_admin"] },
];

export default function Shell() {
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const [sigOpen, setSigOpen] = useState(false);
  const [sigText, setSigText] = useState("");
  const [sigSaving, setSigSaving] = useState(false);
  const [overdue, setOverdue] = useState(0);
  useEffect(() => {
    let live = true;
    const fetchOverdue = () => api.get("/reports/overdue-count").then(r => { if (live) setOverdue(r.data.count || 0); }).catch(() => {});
    fetchOverdue();
    const t = setInterval(fetchOverdue, 60000);
    return () => { live = false; clearInterval(t); };
  }, []);
  const openSig = () => { setSigText(user?.email_signature || ""); setSigOpen(true); };
  const saveSig = async () => {
    setSigSaving(true);
    try {
      const { data } = await api.put("/auth/my-signature", { signature: sigText });
      setUser(u => ({ ...u, email_signature: data.email_signature }));
      toast.success("Signature saved");
      setSigOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save signature");
    } finally { setSigSaving(false); }
  };
  const visibleNav = NAV.filter(i =>
    (!i.roles || i.roles.includes(user?.role)) &&
    (!i.requiresTs || ["admin", "timesheet_admin"].includes(user?.role) || user?.timesheet_access)
  );

  const linkClass = ({ isActive }) => cn(
    "flex items-center gap-3 px-5 py-2.5 text-sm transition-colors border-l-2 border-transparent",
    "text-sidebar-muted hover:text-white hover:bg-white/5",
    "dark:text-muted-foreground dark:hover:text-foreground dark:hover:bg-accent/40",
    isActive && "text-white bg-white/10 border-white dark:text-foreground dark:bg-primary-tint dark:border-primary"
  );

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <IdleManager />
      {/* Sidebar */}
      <aside className="w-60 shrink-0 border-r flex flex-col bg-sidebar text-sidebar-foreground border-sidebar-border dark:bg-background dark:text-foreground dark:border-border">
        <div className="h-14 px-5 border-b border-sidebar-border dark:border-border flex items-center gap-2">
          <button
            data-testid="nav-home-logo"
            onClick={() => nav(homePathFor(user))}
            title="Back to start"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer"
          >
            <div className="h-7 w-7 grid place-items-center bg-white text-sidebar dark:bg-primary dark:text-primary-foreground rounded-sm">
              <TrakLogo size={18} />
            </div>
            <div className="font-display font-bold tracking-tight">TRAK</div>
          </button>
          <span className="ml-auto text-[10px] font-mono text-sidebar-muted dark:text-muted-foreground uppercase">v1.0</span>
        </div>
        <nav className="flex-1 py-3 overflow-y-auto">
          <button
            data-testid="nav-back"
            onClick={() => nav(-1)}
            className="w-full flex items-center gap-3 px-5 py-2.5 text-sm text-sidebar-muted hover:text-white hover:bg-white/5 dark:text-muted-foreground dark:hover:text-foreground dark:hover:bg-accent/40 transition-colors border-l-2 border-transparent"
          >
            <ArrowLeft size={16} weight="regular" /> Back
          </button>
          {visibleNav.map(({ to, label, icon: Icon, exact, testid }) => (
            <NavLink key={to} to={to} end={exact} data-testid={testid} className={linkClass}>
              <Icon size={16} weight="regular" /> {label}
              {to === "/incidents" && overdue > 0 && (
                <span data-testid="nav-overdue-badge" className="ml-auto text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-full bg-rag-red text-background leading-none" title={`${overdue} tickets past resolution SLA`}>{overdue}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="border-t p-4 border-sidebar-border dark:border-border">
          <div className="text-xs font-mono text-sidebar-muted dark:text-muted-foreground">SIGNED IN AS</div>
          <div className="text-sm mt-1">{user?.name}</div>
          <div className="text-[11px] font-mono text-sidebar-muted dark:text-muted-foreground">{user?.email}</div>
          <div className="mt-2 flex items-center justify-between">
            <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border border-sidebar-border dark:border-border">{user?.role}</span>
            <div className="flex items-center gap-3">
              <button
                data-testid="edit-signature-button"
                onClick={openSig}
                className="text-sidebar-muted hover:text-white dark:text-muted-foreground dark:hover:text-primary"
                title="Edit email signature"
              ><PenNib size={16} /></button>
              <button
                data-testid="logout-button"
                onClick={async ()=>{ await logout(); nav("/login", { replace: true }); }}
                className="text-sidebar-muted hover:text-white dark:text-muted-foreground dark:hover:text-primary"
                title="Sign out"
              ><SignOut size={16} /></button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <BreadcrumbProvider>
          <header className="h-14 border-b flex items-center px-6 gap-4 bg-sidebar text-sidebar-foreground border-sidebar-border dark:bg-background dark:text-foreground dark:border-border">
            <Breadcrumbs />
            <div className="ml-auto flex items-center gap-4">
              <ModuleSwitcher />
              <ThemeToggle />
              <div className="hidden md:flex items-center gap-2 text-xs font-mono text-sidebar-muted dark:text-muted-foreground">
                <span className="h-2 w-2 rounded-full bg-rag-green animate-pulse" />
                All systems nominal
              </div>
            </div>
          </header>
          <main className="flex-1 overflow-y-auto">
            <Outlet />
          </main>
        </BreadcrumbProvider>
      </div>

      <Dialog open={sigOpen} onOpenChange={setSigOpen}>
        <DialogContent className="max-w-lg rounded-sm" data-testid="signature-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-mono uppercase text-sm tracking-widest">
              <PenNib size={16} className="text-primary" /> My reply signature
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            This block is appended to the bottom of the email a requester receives when you reply to their ticket.
            Leave it empty to use your team's default signature.
          </p>
          <Textarea
            data-testid="signature-textarea"
            value={sigText}
            onChange={(e)=>setSigText(e.target.value)}
            placeholder={"e.g.\nJane Doe\nSupport Engineer · TRAK Service Desk\nsupport@company.com"}
            className="rounded-sm min-h-[140px] font-mono text-sm"
          />
          <DialogFooter>
            <Button variant="outline" className="rounded-sm text-xs font-mono uppercase" onClick={()=>setSigOpen(false)} data-testid="signature-cancel-btn">Cancel</Button>
            <Button className="rounded-sm text-xs font-mono uppercase" onClick={saveSig} disabled={sigSaving} data-testid="signature-save-btn">
              {sigSaving ? "Saving…" : "Save signature"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
